// frontend/src/components/NotificationBell.js
import React from "react";
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Tooltip,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useNotifications } from "../context/NotificationContext";
import { useNavigate } from "react-router-dom";

export default function NotificationBell({ 
  tooltipTitle = "Notifications",
  sx = {} 
}) {
  const { notifications, markAsRead, markAllAsRead } = useNotifications();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleItemClick = async (item) => {
    handleClose();

    try {
      await markAsRead([item.id]);
    } catch (e) {
      console.warn("Mark as read failed:", e.message);
    }

    if (item.project_id && !item.task_id) {
      navigate(`/projects/${item.project_id}/board`);
    } else if (item.task_id && item.project_id) {
      navigate(`/tasks?project=${item.project_id}`);
    }
  };

  // Build menu items as an array
  const menuItems = [];

  if (notifications.length === 0) {
    menuItems.push(
      <MenuItem key="empty" sx={{ justifyContent: "center", py: 2 }}>
        <ListItemText primary="No notifications" />
      </MenuItem>
    );
  } else {
    // Optional "Mark all as read" button
    if (unreadCount > 0) {
      menuItems.push(
        <MenuItem
          key="mark-all"
          onClick={() => {
            markAllAsRead();
            handleClose();
          }}
          sx={{
            bgcolor: "action.selected",
            "&:hover": { bgcolor: "action.hover" },
            py: 1.5,
          }}
        >
          <ListItemText primary="Mark all as read" />
        </MenuItem>
      );
    }

    // Add divider only if we have real notifications
    menuItems.push(<Divider key="divider" />);

    // Add notification items (newest first, max 20)
    const recentNotifications = [...notifications].reverse().slice(0, 20);
    recentNotifications.forEach((n) => {
      menuItems.push(
        <MenuItem
          key={n.id}
          onClick={() => handleItemClick(n)}
          sx={{
            bgcolor: n.is_read ? "inherit" : "action.hover",
            fontWeight: n.is_read ? "normal" : "600",
            color: "text.primary",
          }}
        >
          <ListItemText
            primary={n.data?.message || n.type}
            secondary={new Date(n.created_at).toLocaleString()}
          />
        </MenuItem>
      );
    });
  }

  return (
    <>
      <Tooltip title={tooltipTitle} arrow placement="bottom">
        <IconButton 
          onClick={handleOpen} 
          size="small" 
          sx={{ 
            color: "#667eea",  // Syncro brand purple
            "&:hover": { 
              color: "#e91e63",  // Syncro brand pink
              bgcolor: "rgba(102, 126, 234, 0.08)"
            },
            transition: "all 0.2s ease",
            ...sx 
          }}
        >
          <Badge 
            badgeContent={unreadCount} 
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                background: "linear-gradient(135deg, #e91e63 0%, #f093fb 100%)",
                fontSize: "0.65rem",
                minWidth: 18,
                height: 18,
                boxShadow: "0 2px 4px rgba(233, 30, 99, 0.4)",
              }
            }}
          >
            <NotificationsIcon sx={{ fontSize: 24 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 1.5 }}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 400,
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            border: "1px solid rgba(0,0,0,0.06)",
            mt: 1,
            "& .MuiMenuItem-root": {
              py: 1.5,
              px: 2,
              borderRadius: 2,
              mx: 1,
              mb: 0.5,
              "&:last-of-type": { mb: 0 },
              "&:hover": { bgcolor: "action.hover" },
            },
            "& .MuiListItemText-primary": {
              fontWeight: 500,
              fontSize: "0.875rem",
            },
            "& .MuiListItemText-secondary": {
              fontSize: "0.75rem",
              color: "text.secondary",
            },
          },
        }}
      >
        {menuItems}
      </Menu>
    </>
  );
}